import React from 'react';
import { Loader, Modal, Row } from '@wunder-mobility/ui-components';
import ErrorFallback from '@/core/components/ErrorHandlers/ErrorFallback';
import { useGoBack } from '@/core/hooks/useGoBack';
import { Trans } from '@lingui/react/macro';
import { FormikProvider, useFormik, Form } from 'formik';
import TripDrivingLogFormFields from '@/features/myTrips/components/TripDrivingLog/TripDrivingLogFormFields';
import { DrivingLogCreateForm } from '@/features/myTrips/types/DrivingLog';
import TripDrivingLogModalActions from '@/features/myTrips/components/TripDrivingLog/TripDrivingLogModalActions';
import { drivingLogSchema } from '@/features/myTrips/utils/validations/drivingLog.schema';
import { useRequestCreateDrivingLogEntryMutation } from '@/features/myTrips/services/DrivingLog.service';
import { useOutletContext } from 'react-router';
import { BookingExtended } from '@/features/booking/types';
import { toDrivingLogEntryRequest } from '@/features/myTrips/utils/toDrivingLogEntryRequest';
import { useBranchTimeZoneId } from '@/features/myTrips/hooks/useBranchTimeZoneId';

const TripDrivingLogCreate = () => {
  const goBack = useGoBack();
  const tripData = useOutletContext<BookingExtended>();
  const [createDrivingLogEntry] = useRequestCreateDrivingLogEntryMutation();
  const { timeZoneId, isError: isTimeZoneError } = useBranchTimeZoneId(tripData.branchId);

  const onSubmit = async (values: DrivingLogCreateForm) => {
    // The form is only rendered once the zone is known; this guards the submit itself.
    if (!timeZoneId) {
      return undefined;
    }

    return createDrivingLogEntry({
      ...toDrivingLogEntryRequest({ ...values, startDate: values.startDate! }, timeZoneId),
      rentalId: tripData.rentalId!,
    })
      .unwrap()
      .then(() => {
        goBack();
      });
  };

  const form = useFormik<DrivingLogCreateForm>({
    initialValues: {
      startDate: undefined,
      startTime: undefined,
      startLocation: undefined,
      endLocation: undefined,
      reason: undefined,
      startKilometers: undefined,
      endKilometers: undefined,
      note: undefined,
      tripType: undefined,
    },
    validationSchema: drivingLogSchema,
    onSubmit,
  });

  return (
    <FormikProvider value={form}>
      <Modal
        modalHeader={<Trans id="myTrips.tripDrivingLogCreate.title">Create driving log</Trans>}
        modalFooter={<TripDrivingLogModalActions mode="create" />}
        variation="small"
        onClose={goBack}
        opened
      >
        {isTimeZoneError && <ErrorFallback />}
        {!isTimeZoneError && !timeZoneId && (
          <Row justify="center">
            <Loader />
          </Row>
        )}
        {timeZoneId && (
          <Form>
            {form.isSubmitting && <Loader cover />}
            <TripDrivingLogFormFields />
          </Form>
        )}
      </Modal>
    </FormikProvider>
  );
};

export default TripDrivingLogCreate;
